import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { A } from '@ember/array';
import IntegrationsBaseController from 'cio/controllers/integrations';
import INTEGRATIONS_METADATA, {
  DataFlow,
  IntegrationCategory,
  IntegrationIDs,
  IntegrationNames,
  IntegrationSyncEnabledStatusMessage,
  IntegrationType,
  StaticIntegration,
} from 'cio/utils/integrations/static-integrations';
import SegmentAuthService from 'cio/services/segment-auth';
import IntegrationModel from 'cio/models/integration';
import EnvironmentService from 'cio/services/environment';
import pluralize from 'cio/utils/pluralize';
import FeaturesService from 'ember-feature-flags/services/features';
import { SqlImportDatabase } from 'cio/utils/sql-import';
import SqlImportModel from 'cio/models/sql-import';
import { mapSqlDatabaseIntegration } from 'cio/utils/sql-import-integrations';

const SHOW_FIRST_WEIGHT = 100;
export default class IndexController extends IntegrationsBaseController {
  @service segmentAuth!: SegmentAuthService;
  @service environment!: EnvironmentService;
  @service features!: FeaturesService;
  // @ts-ignore queryParams in the array format is not typed
  queryParams = [{ queryQP: 'query' }, { selectedCategoryQP: 'selectedCategory' }, { selectedTypeQP: 'selectedType' }];

  public = false;

  @computed(
    'fetchIntegrations.isRunning',
    'segmentOut.isRunning',
    'segmentAuth.segmentIn.isRunning',
    'reportingWebhooks.isRunning',
    'fetchCredentials.isRunning',
    'fetchAdAudiences.isRunning',
    'fetchMparticle.isRunning',
    'fetchCdpOut.isRunning',
    'fetchWarehouseSync.isRunning',
    'fetchSQLImports.isRunning',
    'messageSettings.loadURLParamsCredential.isRunning',
    'loadForms.isRunning',
    'fetchAPIKeys.isRunning',
    'fetchCampaigns.isRunning',
  )
  get loading() {
    return (
      this.fetchIntegrations.isRunning ||
      this.segmentOut.isRunning ||
      this.segmentAuth.segmentIn.isRunning ||
      this.reportingWebhooks.isRunning ||
      this.fetchCredentials.isRunning ||
      this.fetchAdAudiences.isRunning ||
      this.fetchMparticle.isRunning ||
      this.fetchCdpOut.isRunning ||
      this.fetchWarehouseSync.isRunning ||
      this.fetchSQLImports.isRunning ||
      this.messageSettings.loadURLParamsCredential.isRunning ||
      this.loadForms.isRunning ||
      this.fetchAPIKeys.isRunning ||
      this.fetchCampaigns.isRunning
    );
  }

  @computed('fetchCampaigns.last.value')
  get campaigns() {
    return this.fetchCampaigns.last?.value?.toArray() ?? [];
  }

  @computed(
    'fetchIntegrations.last.value',
    'segmentOut.last.value',
    'segmentAuth.segmentIn.lastSuccessful.value',
    'reportingWebhooks.last.value',
    'fetchCredentials.lastSuccessful.value',
    'fetchAdAudiences.last.value',
    'fetchWarehouseSync.lastSuccessful.value',
    'fetchSQLImports.lastSuccessful.value',
    'messageSettings.loadURLParamsCredential.last.value',
    'loadForms.last.value',
    'fetchAPIKeys.last.value',
    'fetchCdpOut.last.value',
  )
  get integrations(): StaticIntegration[] {
    let integrations = this.fetchIntegrations.lastSuccessful?.value ?? ([] as IntegrationModel[]);
    let audienceCredentials = this.fetchCredentials.last?.value?.audienceCredentials?.toArray() ?? [];
    let adwordsCredentials = audienceCredentials.find((cred) => cred.type === 'adwords');
    let facebookCredentials = audienceCredentials.find((cred) => cred.type === 'facebook');
    let audiences = this.fetchAdAudiences.last?.value ?? {
      facebook: [],
      adwords: [],
    };
    let mparticle = this.fetchMparticle.last?.value;
    let warehouseSync = this.fetchWarehouseSync.last?.value;
    let sqlImports = this.fetchSQLImports.last?.value;
    let reportingWebhooks = this.reportingWebhooks.last?.value;
    let formsIntegrationsEnabled = this.loadForms.last?.value ?? {
      facebookLeadAds: false,
      customForms: false,
      typeform: false,
      jotform: false,
      total: 0,
    };
    let apiKeys = this.fetchAPIKeys.last?.value;

    return A(
      [
        (function () {
          let total = (apiKeys?.length ?? 0) as number;

          return {
            id: IntegrationIDs.customerioAPI,
            forceOrder: -1,
            imageSrc: new URL('cio/assets/images/integrations/customerio.svg', import.meta.url).toString(),
            name: IntegrationNames.customerioAPI,
            overview: 'Bring data into Customer.io with Ruby, JavaScript, Go, or other languages',
            enabledStatus: `${total} API ${pluralize(total, 'key')} available`,
            category: IntegrationCategory.api,
            enabled: true,
            beta: false,
            path: 'integrations.customerio',
            type: IntegrationType.native,
            publicType: IntegrationType.existing,
            dataFlow: 'in',
            highlight: true,
            weight: 0,
            total,
          };
        })(),
        {
          id: IntegrationIDs.email,
          forceOrder: 0,
          icon: 'email',
          name: IntegrationNames.email,
          overview: 'Send email in Customer.io',
          category: IntegrationCategory.MessagingChannelTypes,
          enabled: true,
          beta: false,
          path: 'actions.email.sending_domains',
          type: IntegrationType.native,
          publicType: IntegrationType.existing,
        },
        (function () {
          let total = (reportingWebhooks?.length ?? 0) as number;

          return {
            id: IntegrationIDs.reportingWebhooks,
            forceOrder: 0,
            icon: 'webhook',
            name: IntegrationNames.reportingWebhooks,
            overview: 'Send message activity to your servers in real time',
            enabledStatus: `Sending customer and email events to ${total} ${pluralize(total, 'URL')}`,
            category: IntegrationCategory.dataManagement,
            enabled: total > 0,
            beta: false,
            type: IntegrationType.native,
            publicType: IntegrationType.existing,
            path: 'integrations.reporting-webhooks',
            dynamicSegment: undefined,
            dataFlow: 'out',
            highlight: true,
            total,
          };
        })(),
        {
          id: IntegrationIDs.webhooks,
          forceOrder: 0,
          icon: 'webhook',
          name: IntegrationNames.webhooks,
          overview: 'Send custom webhooks in Customer.io',
          category: IntegrationCategory.MessagingChannelTypes,
          enabled: false,
          beta: false,
          path: 'https://customer.io/docs/webhooks-action',
          external: true,
          type: IntegrationType.native,
          publicType: IntegrationType.existing,
        },
        {
          id: IntegrationIDs.mobile,
          forceOrder: 0,
          icon: 'mobile',
          name: IntegrationNames.mobile,
          overview: 'Send mobile push messages in Customer.io',
          category: IntegrationCategory.MessagingChannelTypes,
          enabled: !this.public && this.model.enabledActions.includes('push'),
          beta: false,
          type: IntegrationType.native,
          publicType: IntegrationType.existing,
          path: 'actions.push',
        },
        (function () {
          let enabled = warehouseSync?.type === 's3';
          let paused = warehouseSync?.type === 's3' && warehouseSync?.isPaused;

          return {
            id: IntegrationIDs.amazonS3,
            forceOrder: 0,
            imageSrc: new URL('cio/assets/images/integrations/s3.svg', import.meta.url).toString(),
            name: IntegrationNames.amazonS3,
            overview: 'Publish Customer.io data to S3',
            enabledStatus: paused
              ? IntegrationSyncEnabledStatusMessage.paused
              : IntegrationSyncEnabledStatusMessage.exporting,
            category: IntegrationCategory.databases,
            enabled,
            paused,
            beta: false,
            type: IntegrationType.native,
            publicType: IntegrationType.existing,
            path: 'integrations.warehouse.type',
            dynamicSegment: 's3',
            dataFlow: 'out',
            premiumIntegration: true,
          };
        })(),
        (function () {
          let enabled = warehouseSync?.type === IntegrationIDs.gcs;
          let paused = warehouseSync?.type === IntegrationIDs.gcs && warehouseSync?.isPaused;

          return {
            id: IntegrationIDs.gcs,
            forceOrder: 0,
            imageSrc: new URL('cio/assets/images/integrations/google-cloud-storage.svg', import.meta.url).toString(),
            name: IntegrationNames.gcs,
            overview: 'Publish Customer.io data to Google Cloud Storage',
            enabledStatus: paused
              ? IntegrationSyncEnabledStatusMessage.paused
              : IntegrationSyncEnabledStatusMessage.exporting,
            category: IntegrationCategory.databases,
            enabled,
            paused,
            beta: false,
            type: IntegrationType.native,
            publicType: IntegrationType.existing,
            path: 'integrations.warehouse.type',
            dynamicSegment: IntegrationIDs.gcs,
            dataFlow: 'out',
            premiumIntegration: true,
          };
        })(),
        (function () {
          let enabled = warehouseSync?.type === IntegrationIDs.azureBlob;
          let paused = warehouseSync?.type === IntegrationIDs.azureBlob && warehouseSync?.isPaused;

          return {
            id: IntegrationIDs.azureBlob,
            forceOrder: 0,
            imageSrc: new URL('cio/assets/images/integrations/azure-blob-storage.svg', import.meta.url).toString(),
            name: IntegrationNames.azureBlob,
            overview: 'Publish Customer.io data to Azure Blob Storage',
            enabledStatus: paused
              ? IntegrationSyncEnabledStatusMessage.paused
              : IntegrationSyncEnabledStatusMessage.exporting,
            category: IntegrationCategory.databases,
            enabled,
            paused,
            beta: false,
            type: IntegrationType.native,
            publicType: IntegrationType.existing,
            path: 'integrations.warehouse.type',
            dynamicSegment: IntegrationIDs.azureBlob,
            dataFlow: 'out',
            premiumIntegration: true,
          };
        })(),
        {
          id: IntegrationIDs.yandex,
          forceOrder: 0,
          imageSrc: new URL('cio/assets/images/integrations/yandex.svg', import.meta.url).toString(),
          name: IntegrationNames.yandex,
          overview: 'Publish Customer.io data to Yandex',
          enabledStatus: IntegrationSyncEnabledStatusMessage.exporting,
          category: IntegrationCategory.databases,
          enabled: warehouseSync?.type === IntegrationIDs.yandex,
          paused: warehouseSync?.type === IntegrationIDs.yandex && warehouseSync?.isPaused,
          beta: false,
          type: IntegrationType.native,
          publicType: IntegrationType.existing,
          path: 'integrations.warehouse.type',
          dynamicSegment: IntegrationIDs.yandex,
          dataFlow: 'out',
          premiumIntegration: true,
        },
        {
          id: IntegrationIDs.snowflakeOut,
          forceOrder: 0,
          imageSrc: new URL('cio/assets/images/integrations/snowflake.svg', import.meta.url).toString(),
          name: IntegrationNames.snowflakeOut,
          subtitle: 'Data out',
          overview: 'Publish Customer.io data to Snowflake',
          category: IntegrationCategory.databases,
          enabled: false,
          beta: false,
          type: IntegrationType.native,
          publicType: IntegrationType.existing,
          path: 'integrations.warehouse.snowflake',
          dataFlow: 'out',
          premiumIntegration: true,
        },
        {
          id: IntegrationIDs.bigQueryOut,
          forceOrder: 0,
          imageSrc: new URL('cio/assets/images/integrations/bigquery.svg', import.meta.url).toString(),
          name: IntegrationNames.bigQueryOut,
          subtitle: 'Data out',
          overview: 'Publish Customer.io data to BigQuery',
          category: IntegrationCategory.databases,
          enabled: false,
          beta: false,
          type: IntegrationType.native,
          publicType: IntegrationType.existing,
          path: 'integrations.warehouse.bigquery',
          dataFlow: 'out',
          premiumIntegration: true,
        },
        {
          id: IntegrationIDs.redshiftOut,
          forceOrder: 0,
          imageSrc: new URL('cio/assets/images/integrations/redshift.svg', import.meta.url).toString(),
          name: IntegrationNames.redshiftOut,
          subtitle: 'Data out',
          overview: 'Publish Customer.io data to Redshift',
          category: IntegrationCategory.databases,
          enabled: false,
          beta: false,
          type: IntegrationType.native,
          publicType: IntegrationType.existing,
          path: 'integrations.warehouse.redshift',
          dataFlow: 'out',
          premiumIntegration: true,
        },
        ...this.decoupleSqlIntegrations(sqlImports),
        ...(() => {
          let activeAudiences = audiences.adwords.filterBy('paused', false).length;
          let enabled = !!adwordsCredentials;
          let enabledStatus =
            activeAudiences > 0
              ? `Syncing ${activeAudiences} ${pluralize(activeAudiences, 'segment')}`
              : 'No active syncs';

          return [
            {
              ...INTEGRATIONS_METADATA.adwords,
              enabled: !this.public && enabled,
              enabledStatus,
              dynamicSegment: 'adwords',
              path: adwordsCredentials ? 'integrations.audiences.type.settings' : 'integrations.audiences.type.setup',
            },
            {
              ...INTEGRATIONS_METADATA.youtube,
              enabled,
              enabledStatus,
              dynamicSegment: 'adwords',
              path: adwordsCredentials ? 'integrations.audiences.type.settings' : 'integrations.audiences.type.setup',
            },
          ];
        })(),
        {
          ...INTEGRATIONS_METADATA.googleAnalytics,
          enabled: this.messageSettings.areURLParamsEnabled,
          enabledStatus: 'URL parameters enabled',
          path: 'actions.url_params',
          dynamicSegment: undefined,
        },
        ...(() => {
          let activeAudiences = audiences.facebook.filterBy('paused', false).length;
          let enabled = !!facebookCredentials;
          let enabledStatus =
            activeAudiences > 0
              ? `Syncing ${activeAudiences} ${pluralize(activeAudiences, 'segment')}`
              : 'No active syncs';

          return [
            {
              ...INTEGRATIONS_METADATA.facebook,
              enabled: !this.public && enabled,
              enabledStatus,
              dynamicSegment: 'facebook',
              path: facebookCredentials ? 'integrations.audiences.type.settings' : 'integrations.audiences.type.setup',
            },
            {
              ...INTEGRATIONS_METADATA.instagram,
              enabled,
              enabledStatus,
              dynamicSegment: 'facebook',
              path: facebookCredentials ? 'integrations.audiences.type.settings' : 'integrations.audiences.type.setup',
            },
          ];
        })(),
        (function () {
          let { total } = formsIntegrationsEnabled;

          return {
            id: IntegrationIDs.customForms,
            forceOrder: 0,
            icon: 'form',
            name: IntegrationNames.customForms,
            overview: 'Connect your third-party forms to Customer.io',
            enabledStatus: `${total} ${pluralize(total, 'form')} connected`,
            category: IntegrationCategory.leadGeneration,
            enabled: total > 0,
            beta: false,
            type: IntegrationType.native,
            publicType: IntegrationType.existing,
            path: 'integrations.forms',
            dynamicSegment: undefined,
            highlight: true,
            total,
            dataFlow: 'in',
          };
        })(),
        {
          ...INTEGRATIONS_METADATA.typeform,
          enabled: formsIntegrationsEnabled.typeform,
          path: 'integrations.forms',
          dynamicSegment: undefined,
        },
        {
          ...INTEGRATIONS_METADATA.jotform,
          enabled: formsIntegrationsEnabled.jotform,
          path: 'integrations.forms',
          dynamicSegment: undefined,
        },
        {
          ...INTEGRATIONS_METADATA.facebookLeadAds,
          enabled: !this.public && formsIntegrationsEnabled.facebookLeadAds,
          path: 'integrations.forms',
          dynamicSegment: undefined,
        },
        !this.public && !!this.segmentAuth.segmentIn.lastSuccessful?.value
          ? {
              id: IntegrationIDs.segmentIn,
              forceOrder: 0,
              imageSrc: new URL('cio/assets/images/integrations/segment.svg', import.meta.url).toString(),
              name: IntegrationNames.segmentIn,
              subtitle: 'Destination',
              overview: 'Push your Segment.com data into Customer.io',
              category: IntegrationCategory.dataManagement,
              description: 'Segment allows you to unify customer data and analytics in one place',
              parsedUseCases: ['Push profiles, events, and attributes from Segment to Customer.io'],
              beta: false,
              type: IntegrationType.native,
              publicType: IntegrationType.existing,
              homepage: 'https://segment.io',
              enabled: true,
              path: 'integrations.segment_in',
              dynamicSegment: undefined,
              legacy: true,
              dataFlow: 'in',
            }
          : null,
        {
          ...INTEGRATIONS_METADATA.segmentOut,
          enabled: !this.public && this.segmentOut.last?.value,
          enabledStatus: 'Sending data',
          path: 'integrations.segment_out',
          dynamicSegment: undefined,
        },
        ...(!this.public && this.features.isEnabled('cdp_enabled')
          ? [
              // CDP IN
              {
                ...INTEGRATIONS_METADATA.cdpOut,
                weight: SHOW_FIRST_WEIGHT,
                id: IntegrationIDs.cdpIn,
                dataFlow: DataFlow.in,
                enabled: !this.public && (this.fetchCdpOut.last?.value ?? false),
                enabledStatus: 'Receiving data',
                path: 'integrations.cdp_in',
                dynamicSegment: undefined,
              },
              // CDP OUT
              {
                ...INTEGRATIONS_METADATA.cdpOut,
                weight: SHOW_FIRST_WEIGHT,
                enabled: !this.public && (this.fetchCdpOut.last?.value ?? false),
                enabledStatus: 'Sending data',
                path: 'integrations.cdp_out',
                dynamicSegment: undefined,
              },
            ]
          : []),
        {
          ...INTEGRATIONS_METADATA.mparticleOut,
          enabled: !!mparticle,
          enabledStatus: 'Sending email events',
          path: 'integrations.mparticle_out',
          dynamicSegment: undefined,
        },
        {
          ...INTEGRATIONS_METADATA.twilio,
          enabled: !this.public && this.model.enabledActions.includes('twilio'),
          path: 'actions.twilio',
          dynamicSegment: undefined,
        },
        {
          ...INTEGRATIONS_METADATA.slack,
          enabled: !this.public && this.model.enabledActions.includes('slack'),
          path: 'actions.slack',
          dynamicSegment: undefined,
        },

        // Filter out some data warehouse integrations temporarily because
        // we're adding them manually above. When the code is deployed to
        // production, these can be removed from the Integrations excel sheet,
        // and then the filtering below can be removed as well.
        ...integrations
          .filter(
            (i) =>
              ![
                'Amazon_S3',
                'Google_Cloud_Storage',
                'Azure_Blob_Storage',
                'Google_BigQuery',
                'Amazon_Redshift',
                'Snowflake',
                'MySQL',
                'PostgreSQL',
              ].includes(i.id),
          )
          .toArray(),
      ].compact(),
    ) as StaticIntegration[];
  }

  @action
  checkNativeDataOutStatuses() {
    let integrations = this.fetchIntegrations.lastSuccessful?.value ?? ([] as IntegrationModel[]);

    integrations.forEach((integration) => {
      integration.checkNativeDataOutStatus();
    });
  }

  private decoupleSqlIntegrations(sqlImports?: Record<SqlImportDatabase, SqlImportModel[]> | null) {
    if (sqlImports == null) {
      return [];
    }
    let returnArray = [] as any;

    for (let [dbName, sqlImportModel] of Object.entries(sqlImports)) {
      returnArray.push(mapSqlDatabaseIntegration(sqlImportModel, dbName as SqlImportDatabase));
    }

    return returnArray;
  }
}

declare module '@ember/controller' {
  interface Registry {
    'integrations.index': IndexController;
  }
}
